// eslint-disable-next-line import/no-cycle
import {
  WidgetTypeConfig,
  ConfigurationPanelStructureItemEnum,
  Property,
  TemplateStructureEnum,
  ElementPartTypeEnum,
  ComponentNameEnum,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import {
  setAttribute,
  setContactProperty,
  setContent,
  setDefaultCountry,
  setInputType,
  setValidationRules,
  saveFormFieldConfig,
  toggleAttribute,
  toggleClass,
  toggleShowLabelInside,
} from '@/components/template-builder/setters';
import { countriesList, getCountryName } from '@/components/template-builder/utils/countries-list';
// eslint-disable-next-line import/no-cycle
import {
  getAttributeByName,
  getContactProperty,
  getContentWithLineBreaks,
  getValidationRules,
  hasAttribute,
  hasClass,
} from '@/components/template-builder/getters';
import variables from '@/components/template-builder/utils/variables-list';
// eslint-disable-next-line import/no-cycle
import getInputVariablesFieldConfig from '@/components/template-builder/config/fields/input-variables-config';
// eslint-disable-next-line import/no-cycle
import getDisplayFieldConfig from '@/components/template-builder/config/fields/display-config';
// eslint-disable-next-line import/no-cycle
import getDropdownFieldConfig from '@/components/template-builder/config/fields/dropdown-config';
// eslint-disable-next-line import/no-cycle
import { getFormFieldContactPropertyConfig } from '@/components/template-builder/config/fields/form-fields-config';
// eslint-disable-next-line import/no-cycle
import getTriggerButtonFieldConfig from '@/components/template-builder/config/fields/trigger-button-config';
// eslint-disable-next-line import/no-cycle
import { toggleDesignPanel } from '@/components/template-builder/callbacks';
// eslint-disable-next-line import/no-cycle
import getWidgetCommonStylesGroup from '@/components/template-builder/config/fields/widget-common-styles-config';

const widgetInputTextConfig: WidgetTypeConfig = {
  tabPanel: {
    label: 'templateBuilder.widgetsHeaders.inputText',
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
        label: 'templateBuilder.configs.widgetConfiguration',
        selector: 'body #spm_body #{ID}',
        items: [
          {
            label: 'templateBuilder.formFields.contactProperty',
            selector: '{SELECTOR}',
            properties: [{ name: 'contactProperty', getter: getContactProperty, setters: [setContactProperty] }],
            fieldId: 'contactProperty',
            ...getFormFieldContactPropertyConfig({ typeWidget: 'inputText' }),
          },
          {
            label: 'templateBuilder.formFields.inputType',
            selector: '{SELECTOR} .spm_widget_input_text__input',
            properties: [{ name: 'type', getter: getAttributeByName, setters: [setInputType] }],
            fieldId: 'inputType',
            ...getDropdownFieldConfig(
              [
                { value: 'text', label: 'templateBuilder.formFields.inputTypeOptions.text' },
                { value: 'url', label: 'templateBuilder.formFields.inputTypeOptions.url' },
                { value: 'email', label: 'templateBuilder.formFields.inputTypeOptions.email' },
                { value: 'tel', label: 'templateBuilder.formFields.inputTypeOptions.tel' },
                { value: 'number', label: 'templateBuilder.formFields.inputTypeOptions.number' },
              ],
              'text',
            ),
          },
          {
            label: 'templateBuilder.formFields.defaultCountry',
            selector: '{SELECTOR}',
            properties: [{
              name: 'data-default-country',
              getter: getAttributeByName,
              setters: [
                setDefaultCountry,
                (selector: string, property: Property) => saveFormFieldConfig(selector, property, 'defaultCountry'),
              ],
            }],
            fieldId: 'defaultCountry',
            parentFieldId: 'inputType',
            isVisible: (items: any[]) => {
              const typeField = items.find((item: any) => item.fieldId === 'inputType');
              const typeValue = typeField?.properties?.[0]?.value;
              return typeValue === 'tel';
            },
            ...getDropdownFieldConfig(
              [...countriesList]
                .sort((a, b) => getCountryName(a.code).localeCompare(getCountryName(b.code)))
                .map((country) => ({
                  label: `${country.flag} ${getCountryName(country.code)} (${country.dialCode})`,
                  value: country.code,
                })),
              'FR',
              true,
            ),
          },
          {
            label: 'templateBuilder.formFields.validationRules',
            selector: '{SELECTOR}',
            properties: [{ name: 'validationRules', getter: getValidationRules, setters: [setValidationRules] }],
            fieldId: 'validationRules',
            componentName: ComponentNameEnum.VALIDATION_RULES,
            options: {},
            watchParserValues: true,
          },
          {
            label: 'templateBuilder.formFields.showLabel',
            selector: '{SELECTOR} .spm_widget_input_text__container',
            properties: [
              {
                name: 'spm_label_inside',
                getter: (selector: string, name: string) => hasClass(selector, name, { revert: true }),
                setters: [
                  (selector: string, property: Property) => toggleClass(
                    selector,
                    property,
                    {
                      revert: true,
                      callback: () => toggleShowLabelInside(selector, property),
                    },
                  ),
                ],
              },
            ],
            fieldId: 'showLabelInside',
            ...getDisplayFieldConfig('spm_label_inside'),
            displayToggle: false,
          },
          {
            label: 'templateBuilder.formFields.label',
            selector: '{SELECTOR} .spm_widget_input_text__label',
            properties: [{ name: 'content', getter: getContentWithLineBreaks, setters: [setContent] }],
            fieldId: 'label',
            ...getInputVariablesFieldConfig('Libellé', variables.allVariables, false, true),
          },
          {
            label: 'templateBuilder.formFields.inputPlaceholder',
            selector: '{SELECTOR} .spm_widget_input_text__input',
            properties: [{ name: 'placeholder', getter: getAttributeByName, setters: [setAttribute] }],
            fieldId: 'placeholder',
            ...getInputVariablesFieldConfig('Texte de remplissage', variables.allVariables, false, true),
          },

          {
            label: 'templateBuilder.formFields.fieldRequired',
            selector: '{SELECTOR} .spm_widget_input_text__input',
            properties: [{ name: 'required', getter: hasAttribute, setters: [toggleAttribute] }],
            fieldId: 'fieldRequired',
            ...getDisplayFieldConfig('required'),
            displayToggle: false,
            allowEmpty: false,
          },
        ],
      },
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.GROUP,
        label: 'templateBuilder.configs.widgetStyles',
        canSave: true,
        elementPartType: ElementPartTypeEnum.STYLES,
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.configs.widgetBlockStyles',
              selector: '',
              items: [
                {
                  label: 'templateBuilder.formFields.stylesInGlobalFormConfig',
                  selector: '',
                  properties: [],
                  componentName: ComponentNameEnum.INFORMATION,
                  options: {
                    severity: 'secondary',
                  },
                },
                {
                  label: 'templateBuilder.formFields.goToGlobalFormStyles',
                  selector: '',
                  properties: [],
                  ...getTriggerButtonFieldConfig(
                    'templateBuilder.formFields.goToGlobalFormStyles',
                    'templateBuilder.formFields.goToGlobalFormStyles',
                    () => toggleDesignPanel('pageInputs'),
                  ),
                },
              ],
            },
            getWidgetCommonStylesGroup(),
          ],
        },
      },
    ],
  },
};

export default widgetInputTextConfig;
